import { Application } from '@/types/application';
import { Detail, emptyValue } from '@/types/detail';
import { Configuration, Metadata } from '@/types/metadata/metadata';
import { ValidationError, ValidationObject } from '@/types/validation';
import { VariableRef } from '@/types/variable';
import { isEmptyValue, isKeyValue, isObject, isVariable } from '@/util/typeGuards';
import { getChipType } from '@/util/variables';
import { variableExists } from './validation';

export const variableUtil = (): ValidationObject<VariableRef | emptyValue | undefined> => {
  const validateVariable = (
    application: Application,
    metadata: Metadata,
    linkedSubroutineId: number,
    nodeId: number,
    blockName: string,
    configuration: Configuration,
    details: Detail
  ): ValidationError[] => {
    const errors: ValidationError[] = [];
    const variable = getValue(configuration, details);
    if (!variable && configuration.required) {
      errors.push({
        severity: 'error',
        message: `Block ${blockName} is missing required variable: ${configuration.label}`,
        id: nodeId
      });
    }
    if (variable && !isEmptyValue(variable)) {
      if (!variableExists(application.variables, application.subroutines, metadata, variable, linkedSubroutineId)) {
        errors.push({
          severity: 'error',
          message: `Block ${blockName} is referring to a variable that doesn't exist within its context: ${configuration.label}`,
          id: nodeId
        });
      }

      const variableType = getChipType(variable, application, metadata, linkedSubroutineId);
      const expectedType = configuration.subtype ?? configuration.type;
      if (
        variableType &&
        expectedType &&
        expectedType !== 'any' &&
        variableType !== expectedType &&
        metadata.datatypes?.find((datatype) => datatype.type === expectedType)
      ) {
        errors.push({
          severity: 'error',
          message: `Block ${blockName} requires type: ${expectedType} but variable is of type: ${variableType}`,
          id: nodeId
        });
      }
    }

    return errors;
  };

  const getValue = (configuration: Configuration, initVal: any): VariableRef | emptyValue | undefined => {
    if (!initVal) return;
    if (isVariable(initVal) || isEmptyValue(initVal)) {
      return initVal;
    }
    if (isKeyValue(initVal) && isVariable(initVal.value)) {
      return initVal.value;
    }
    if (isObject(initVal)) {
      const prop = initVal[configuration.value];
      return getValue(configuration, prop);
    }
    return;
  };

  return {
    validate: validateVariable,
    getValue: getValue
  };
};
